/**
 * DefinitionPopover — floating panel that appears when clicking on the
 * ▶ Run button of a ::define block in the CodeMirror editor.
 * Shows plugin info, ConfigPanel (visual or raw JSON), output, and run actions.
 */

import type { UnitConfigContext } from "@magic-md/extension-sdk";
import type {
    ActionDeclaration,
    ConfigPanelContext,
} from "@magic-md/plugin-sdk";
import { JsonTree } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import { MoveDiagonal2, Play, X } from "lucide-react";
import {
    type KeyboardEvent as ReactKeyboardEvent,
    type MouseEvent as ReactMouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDefinitionUseBinding } from "../hooks/useDefinitionUseBinding";
import { useDocumentAssetReferences } from "../hooks/useDocumentAssetReferences";
import { useDraftConfig } from "../hooks/useDraftConfig";
import { useDraggablePopover } from "../hooks/useDraggablePopover";
import { usePluginUI } from "../hooks/usePluginUI";
import { attachDefinitionOutputs, buildConfigPanelUnits } from "../lib/config-panel-units";
import { collectParsedDocumentUseBindings } from "../lib/for-scope-units";
import { pickerInitialPathFromDocument } from "../lib/path-utils";
import { getDocumentBundleDir } from "../lib/publish-utils";
import { runDefinitionWithFallback } from "../lib/run-definition";
import {
    isEditorTargetProvenanceCurrent,
    resolveSerializerTargetLocator,
    type EditorSerializerTargetLocator,
    type SerializerUnitKind,
} from "../lib/unit-edit-target";
import {
    collectScopeVariables,
    collectTemplateInputScopeVariables,
} from "../lib/scope-variables";
import { cn } from "../lib/utils";
import { ExtensionUnitConfigActions } from "../extensions/ExtensionContributions";
import { useExtensionUnitConfigActions } from "../extensions/useExtensionContributions";
import { getPlatformService } from "../platform";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { showFilePickerDialog, showFolderPickerDialog } from "./dialogs";
import { PaneRenderProvider, usePaneContext } from "./PaneContext";
import {
    ConfigEditorModeButton,
    DefinitionPanelTabs,
    DirtyStateBanner,
    RawConfigEditor,
    SecondaryActionsBar,
    StatusDot,
} from "./shared";
import { UseBindEditor } from "./UseBindEditor";
import { UseWithEditor } from "./UseWithEditor";

export interface PopoverTarget {
    definitionId: string;
    /** Position to show the popover (viewport coords) */
    x: number;
    y: number;
    /** Exact source occurrence captured by the CodeMirror widget. */
    locator?: EditorSerializerTargetLocator;
    unitKind?: Extract<SerializerUnitKind, "define" | "use">;
    tabId?: string;
    paneId?: string;
    filePath?: string;
}

interface PopoverSize {
    width: number;
    /** null means content-driven height until the user resizes the popover. */
    height: number | null;
}

interface ResolvedPopoverSize {
    width: number;
    height: number;
}

interface PopoverResizeState {
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    originX: number;
    originY: number;
}

const CONFIG_POPOVER_MARGIN = 8;
const CONFIG_POPOVER_MIN_SIZE: ResolvedPopoverSize = { width: 360, height: 260 };
const DEFINITION_CONFIG_POPOVER_DEFAULT = { width: 520, estimatedHeight: 360 };
const CONTENT_CONFIG_POPOVER_DEFAULT = { width: 400, estimatedHeight: 320 };
const CONFIG_POPOVER_HEADER_CLASS =
    "flex h-10 items-center gap-1.5 border-b border-border bg-muted/50 px-3 cursor-grab select-none active:cursor-grabbing";
const EMPTY_UNIT_CONFIG_CONTEXT: UnitConfigContext = {
    definitionId: "",
    plugin: "",
    config: {},
};

function samePopoverDefault(
    a: typeof DEFINITION_CONFIG_POPOVER_DEFAULT,
    b: typeof DEFINITION_CONFIG_POPOVER_DEFAULT,
) {
    return a.width === b.width && a.estimatedHeight === b.estimatedHeight;
}

function getConfigPopoverMaxHeight(origin: { y: number }): number {
    const viewportHeight = typeof window === "undefined" ? 768 : window.innerHeight;
    return Math.max(
        CONFIG_POPOVER_MIN_SIZE.height,
        viewportHeight - origin.y - CONFIG_POPOVER_MARGIN,
    );
}

function clampConfigPopoverSize(
    size: ResolvedPopoverSize,
    origin: { x: number; y: number },
): ResolvedPopoverSize {
    const viewportWidth = typeof window === "undefined" ? 1024 : window.innerWidth;
    const maxWidth = Math.max(
        CONFIG_POPOVER_MIN_SIZE.width,
        viewportWidth - origin.x - CONFIG_POPOVER_MARGIN,
    );
    const maxHeight = getConfigPopoverMaxHeight(origin);

    return {
        width: Math.min(Math.max(size.width, CONFIG_POPOVER_MIN_SIZE.width), maxWidth),
        height: Math.min(Math.max(size.height, CONFIG_POPOVER_MIN_SIZE.height), maxHeight),
    };
}

function ConfigPopoverResizeHandle({
    isResizing,
    onMouseDown,
    onKeyDown,
}: {
    isResizing: boolean;
    onMouseDown: (event: ReactMouseEvent<HTMLButtonElement>) => void;
    onKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
}) {
    const t = useT("editor");
    return (
        <button
            type="button"
            data-no-drag="true"
            aria-label={t("definition.resizeConfig")}
            title={t("definition.resizeConfig")}
            className={cn(
                "absolute bottom-1 right-1 z-20 flex h-5 w-5 cursor-nwse-resize items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground focus-visible:outline-none",
                isResizing && "bg-muted text-foreground",
            )}
            onMouseDown={onMouseDown}
            onKeyDown={onKeyDown}
        >
            <MoveDiagonal2 size={11} aria-hidden="true" />
        </button>
    );
}

export function DefinitionPopover({
    target,
    onClose,
}: {
    target: PopoverTarget | null;
    onClose: () => void;
}) {
    if (!target) return null;
    return (
        <PaneRenderProvider>
            <DefinitionPopoverInner target={target} onClose={onClose} />
        </PaneRenderProvider>
    );
}

function DefinitionPopoverInner({
    target,
    onClose,
}: {
    target: PopoverTarget;
    onClose: () => void;
}) {
    const t = useT("editor");
    const ref = useRef<HTMLDivElement>(null);
    const previousDefinitionIdRef = useRef<string | null>(null);
    const [activeTab, setActiveTab] = useState<"config" | "output">("config");
    const [useRawEditor, setUseRawEditor] = useState(false);
    // Use pane-scoped definitions & executions (not global parsedDocument)
    const paneCtx = usePaneContext();
    const definitions = paneCtx.definitions;
    const definitionOutputs = paneCtx.definitionOutputs;
    const contentDefinitions = paneCtx.contentDefinitions;
    const importDocuments = paneCtx.importDocuments;
    const parsedDocument = paneCtx.parsedDocument;
    const documentPath = paneCtx.tab?.filePath ?? null;
    const provenanceMatches = !target.tabId || isEditorTargetProvenanceCurrent(
        target as Required<Pick<PopoverTarget, "tabId">> & PopoverTarget,
        {
            tabId: paneCtx.tabId,
            paneId: paneCtx.paneId,
            filePath: paneCtx.tab?.filePath,
        },
    );
    const validatedLocator = useMemo(() => {
        if (!target.locator) return undefined;
        if (!target.locator.fingerprint) return target.locator;
        if (!target.unitKind || !paneCtx.tab?.content || !provenanceMatches) {
            return undefined;
        }
        return resolveSerializerTargetLocator({
            content: paneCtx.tab.content,
            unitKind: target.unitKind,
            unitId: target.definitionId,
            locator: target.locator,
            fallbackOccurrence: target.unitKind === "use" ? "first" : "last",
        });
    }, [paneCtx.tab?.content, provenanceMatches, target]);
    const targetUsable = provenanceMatches
        && (!target.locator?.fingerprint || !!validatedLocator);
    const selectedDefinitionId = targetUsable ? target.definitionId : null;
    const documentBundlePath = documentPath ? getDocumentBundleDir(documentPath) : null;
    const pickerInitialPath = pickerInitialPathFromDocument(paneCtx.tab?.filePath);
    const availableAssets = useDocumentAssetReferences(paneCtx.tab?.filePath, importDocuments);
    const definitionsWithOutputs = useMemo(
        () => attachDefinitionOutputs(definitions, definitionOutputs),
        [definitions, definitionOutputs],
    );
    const availableUnits = useMemo(
        () => buildConfigPanelUnits({
            definitions: definitionsWithOutputs,
            definitionOutputs,
            forDefinitions: paneCtx.forDefinitions,
            contentDefinitions,
            imports: parsedDocument?.imports,
            uses: collectParsedDocumentUseBindings(parsedDocument),
            forExecutions: paneCtx.forExecutions,
            contentExecutions: paneCtx.contentExecutions,
            importExecutions: paneCtx.importExecutions,
            useExecutions: paneCtx.useExecutions,
        }),
        [
            definitionsWithOutputs,
            definitionOutputs,
            paneCtx.forDefinitions,
            contentDefinitions,
            parsedDocument,
            paneCtx.forExecutions,
            paneCtx.contentExecutions,
            paneCtx.importExecutions,
            paneCtx.useExecutions,
        ],
    );

    const executions = paneCtx.executions;
    const execution = useMemo(
        () => (selectedDefinitionId
            ? (executions.get(selectedDefinitionId) ?? null)
            : null),
        [executions, selectedDefinitionId],
    );
    const runPrimary = usePluginExecutionStore((s) => s.runPrimary);
    const runAction = usePluginExecutionStore((s) => s.runAction);
    const cancelExecution = usePluginExecutionStore((s) => s.cancelExecution);
    const clearExecution = usePluginExecutionStore((s) => s.clearExecution);

    const definition = selectedDefinitionId
        ? definitions.get(selectedDefinitionId)
        : undefined;
    const plugin = usePluginUI(definition?.pluginName);
    const defaultPopover = definition
        ? DEFINITION_CONFIG_POPOVER_DEFAULT
        : CONTENT_CONFIG_POPOVER_DEFAULT;
    const defaultPopoverRef = useRef(defaultPopover);
    const [popoverSize, setPopoverSize] = useState<PopoverSize>({
        width: defaultPopover.width,
        height: null,
    });
    const [measuredPopoverHeight, setMeasuredPopoverHeight] = useState<number | null>(null);

    useEffect(() => {
        if (samePopoverDefault(defaultPopoverRef.current, defaultPopover)) return;
        defaultPopoverRef.current = defaultPopover;
        setPopoverSize({ width: defaultPopover.width, height: null });
        setMeasuredPopoverHeight(null);
    }, [defaultPopover]);

    const effectivePopoverHeight =
        popoverSize.height ?? measuredPopoverHeight ?? defaultPopover.estimatedHeight;

    const { isDragging, onHeaderMouseDown, position } = useDraggablePopover({
        anchor: target ? { x: target.x, y: target.y } : null,
        width: popoverSize.width,
        height: effectivePopoverHeight,
    });

    const resizeRef = useRef<PopoverResizeState | null>(null);
    const [isResizing, setIsResizing] = useState(false);

    const currentPopoverOrigin = useCallback(() => ({
        x: position?.x ?? target?.x ?? CONFIG_POPOVER_MARGIN,
        y: position?.y ?? (target ? target.y + 4 : CONFIG_POPOVER_MARGIN),
    }), [position, target]);

    const getResizablePopoverHeight = useCallback(
        () => popoverSize.height ?? measuredPopoverHeight ?? defaultPopover.estimatedHeight,
        [defaultPopover.estimatedHeight, measuredPopoverHeight, popoverSize.height],
    );

    const stopResizing = useCallback(() => {
        resizeRef.current = null;
        setIsResizing(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    }, []);

    const startPopoverResize = useCallback(
        (event: ReactMouseEvent<HTMLButtonElement>) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            const origin = currentPopoverOrigin();
            resizeRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                startWidth: popoverSize.width,
                startHeight: getResizablePopoverHeight(),
                originX: origin.x,
                originY: origin.y,
            };
            setIsResizing(true);
            document.body.style.cursor = "nwse-resize";
            document.body.style.userSelect = "none";
        },
        [currentPopoverOrigin, getResizablePopoverHeight, popoverSize.width],
    );

    const nudgePopoverSize = useCallback(
        (deltaWidth: number, deltaHeight: number) => {
            const origin = currentPopoverOrigin();
            const currentHeight = getResizablePopoverHeight();
            setPopoverSize((current) =>
                clampConfigPopoverSize(
                    {
                        width: current.width + deltaWidth,
                        height: currentHeight + deltaHeight,
                    },
                    origin,
                ),
            );
        },
        [currentPopoverOrigin, getResizablePopoverHeight],
    );

    const handleResizeKeyDown = useCallback(
        (event: ReactKeyboardEvent<HTMLButtonElement>) => {
            const step = event.shiftKey ? 40 : 16;
            if (event.key === "ArrowRight") {
                event.preventDefault();
                nudgePopoverSize(step, 0);
            } else if (event.key === "ArrowLeft") {
                event.preventDefault();
                nudgePopoverSize(-step, 0);
            } else if (event.key === "ArrowDown") {
                event.preventDefault();
                nudgePopoverSize(0, step);
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                nudgePopoverSize(0, -step);
            }
        },
        [nudgePopoverSize],
    );

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            const resize = resizeRef.current;
            if (!resize) return;
            setPopoverSize(
                clampConfigPopoverSize(
                    {
                        width: resize.startWidth + event.clientX - resize.startX,
                        height: resize.startHeight + event.clientY - resize.startY,
                    },
                    { x: resize.originX, y: resize.originY },
                ),
            );
        };

        const handleMouseUp = () => {
            if (!resizeRef.current) return;
            stopResizing();
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            stopResizing();
        };
    }, [stopResizing]);

    useEffect(() => {
        if (!target && isResizing) stopResizing();
    }, [isResizing, stopResizing, target]);

    useEffect(() => {
        if (!target || popoverSize.height !== null) return;
        const element = ref.current;
        if (!element) return;

        const measure = () => {
            const nextHeight = Math.round(element.getBoundingClientRect().height);
            if (nextHeight <= 0) return;
            setMeasuredPopoverHeight((current) =>
                current === nextHeight ? current : nextHeight,
            );
        };

        measure();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [target, popoverSize.height]);

    // Reset tab state when target changes
    useEffect(() => {
        if (previousDefinitionIdRef.current === selectedDefinitionId) return;
        previousDefinitionIdRef.current = selectedDefinitionId;
        setActiveTab("config");
        setUseRawEditor(false);
        setMeasuredPopoverHeight(null);
        setPopoverSize((current) => (
            current.height === null ? current : { ...current, height: null }
        ));
    }, [selectedDefinitionId]);

    useEffect(() => {
        if (!targetUsable) onClose();
    }, [onClose, targetUsable]);

    // Close on outside click
    useEffect(() => {
        if (!target) return;
        const handler = (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            // Ignore clicks inside ComboSelect portal dropdowns
            if (t.closest?.("[data-comboselect-portal]")) return;
            // Ignore clicks inside dialog portals (file picker, folder picker, etc.)
            if (t.closest?.("[data-dialog-portal]")) return;
            if (ref.current && !ref.current.contains(t)) {
                onClose();
            }
        };
        const timer = setTimeout(
            () => document.addEventListener("mousedown", handler),
            50,
        );
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handler);
        };
    }, [target, onClose]);

    // Close on Escape
    useEffect(() => {
        if (!target) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [target, onClose]);

    const {
        currentUseBinding,
        useSourceConfig,
        importSymbols,
        templateParams,
        templateInputSchema,
        missingRequiredTemplateInputs,
        useSourceContent,
    } =
        useDefinitionUseBinding(
            selectedDefinitionId ?? undefined,
            parsedDocument ?? undefined,
            definitions,
            contentDefinitions,
            paneCtx.importExecutions,
            {
                content: paneCtx.tab?.content,
                locator: validatedLocator,
            },
        );

    const { draftConfig, isDirty, updateDraft, applyDraft, discardDraft } =
        useDraftConfig(
            selectedDefinitionId ?? undefined,
            definition?.config as Record<string, unknown> | undefined,
            {
                unitKind: currentUseBinding ? "use" : "define",
                ...(useSourceConfig ? {
                    sourceConfig: useSourceConfig,
                    bind: currentUseBinding?.bind,
                    with: currentUseBinding?.with,
                } : {}),
                locator: validatedLocator,
                sourceRange: currentUseBinding?.range,
            },
        );

    const runForAction = usePluginExecutionStore((s) => s.runForAction);
    const isTemplateUnit = currentUseBinding
        ? currentUseBinding.template === true || missingRequiredTemplateInputs.length > 0
        : !!definition?.template;

    const handleRun = useCallback(() => {
        if (!definition || isTemplateUnit) return;

        if (currentUseBinding) {
            if (isDirty) applyDraft();
            void usePluginExecutionStore.getState().runUseAction(
                currentUseBinding.id,
            );
            return;
        }

        void runDefinitionWithFallback({
            definition,
            isDirty,
            applyDraft,
            runPrimary,
            runForAction,
        });
    }, [
        definition,
        isDirty,
        applyDraft,
        runPrimary,
        runForAction,
        currentUseBinding,
        isTemplateUnit,
    ]);

    // Build ConfigPanelContext for the plugin's ConfigPanel
    const scopeVariables = useMemo(
        () => {
            if (!definition) return [];
            return [
                ...collectTemplateInputScopeVariables(
                    definition.inputSchema,
                    definition.params,
                    draftConfig,
                ),
                ...collectScopeVariables(
                    definition.id,
                    parsedDocument?.blocks ?? [],
                    parsedDocument?.forDefinitions,
                    definitions,
                    {
                        uses: collectParsedDocumentUseBindings(parsedDocument),
                        contentDefinitions,
                    },
                ),
            ];
        },
        [definition, draftConfig, parsedDocument, definitions, contentDefinitions],
    );

    const configContext = useMemo<ConfigPanelContext>(
        () => ({
            currentDefinitionId: definition?.id ?? "",
            documentPath,
            documentBundlePath,
            scopeVariables,
            selectFile: async (options) => {
                const result = await showFilePickerDialog({
                    accept: options?.accept,
                    initialPath: pickerInitialPath,
                });
                return result ?? "";
            },
            selectDirectory: async () => {
                const result = await showFolderPickerDialog({
                    initialPath: pickerInitialPath,
                });
                return result ?? "";
            },
            getAvailableDefinitions: () => definitionsWithOutputs,
            getAvailableUnits: () => availableUnits,
            getAvailableContentDefinitions: () => contentDefinitions,
            getAvailableImportDocuments: () => importDocuments,
            getAvailableAssets: () => availableAssets,
            previewAction: async (actionName: string) => {
                if (!definition) return { success: false, message: "No definition" };
                if (isTemplateUnit) {
                    return { success: false, message: t("instance.templateNotExecutable") };
                }
                return runAction(definition, actionName, definitions);
            },
            callSidecar: async (sidecarId, method, params) =>
                getPlatformService().engine.sidecarCall(sidecarId, method, params),
        }),
        [
            definitions,
            definitionsWithOutputs,
            availableUnits,
            contentDefinitions,
            importDocuments,
            definition,
            runAction,
            isTemplateUnit,
            scopeVariables,
            pickerInitialPath,
            availableAssets,
            documentPath,
            documentBundlePath,
            t,
        ],
    );
    const unitConfigContext = useMemo<UnitConfigContext>(
        () =>
            definition
                ? {
                      definitionId: definition.id,
                      plugin: definition.pluginName,
                      config: (definition.config ?? {}) as object,
                      output: execution?.output,
                  }
                : EMPTY_UNIT_CONFIG_CONTEXT,
        [definition, execution?.output],
    );
    const extensionUnitActions = useExtensionUnitConfigActions(unitConfigContext);

    if (!target) return null;

    // Content-sourced ::use — no plugin definition, show simplified popover with bind/with editors
    if (!definition && currentUseBinding) {
        const headerBadge = useSourceContent?.template ? "TEMPLATE" : "CONTENT";
        const headerTitle = useSourceContent?.template
            ? t("definition.templateInstance")
            : t("definition.contentInstance");
        const style: React.CSSProperties = {
            position: "fixed",
            left: position?.x ?? target.x,
            top: position?.y ?? target.y + 4,
            zIndex: 100,
            width: popoverSize.width,
            maxHeight: getConfigPopoverMaxHeight(currentPopoverOrigin()),
            ...(popoverSize.height !== null ? { height: popoverSize.height } : {}),
        };
        const hasAnyConfig = !!(useSourceConfig || templateParams || templateInputSchema);
        return (
            <div
                ref={ref}
                style={style}
                data-magic-md-config-popover="content"
                className="relative flex flex-col rounded-xl border border-border bg-background shadow-xl overflow-hidden"
            >
                {/* Header */}
                <div
                    data-magic-md-config-popover-header
                    className={CONFIG_POPOVER_HEADER_CLASS}
                    role="presentation"
                    onMouseDownCapture={onHeaderMouseDown}
                    title={t("definition.dragMoveConfig")}
                >
                    <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-primary">
                        {headerBadge}
                    </span>
                    <span
                        className="min-w-0 flex-1 truncate text-[11px] font-semibold"
                        title={`${headerTitle}: ${currentUseBinding.source}`}
                    >
                        {currentUseBinding.id}
                    </span>
                    {isDragging && (
                        <span className="text-[10px] text-muted-foreground/70 shrink-0">
                            {t("definition.moving")}
                        </span>
                    )}
                    <button
                        type="button"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                        onClick={onClose}
                    >
                        <X size={14} />
                    </button>
                </div>
                {/* Content */}
                <div className="min-h-0 flex-auto overflow-auto">
                    {hasAnyConfig ? (
                        <div className="p-4 space-y-3">
                            {useSourceConfig && (
                                <UseBindEditor
                                    useBinding={currentUseBinding}
                                    definitions={definitions}
                                    sourceConfig={useSourceConfig}
                                    importSymbols={importSymbols}
                                    locator={validatedLocator}
                                />
                            )}
                            {(templateParams || templateInputSchema) && (
                                <UseWithEditor
                                    useBinding={currentUseBinding}
                                    templateParams={templateParams ?? []}
                                    inputSchema={templateInputSchema}
                                    definitions={definitions}
                                    variant="plain"
                                    locator={validatedLocator}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="p-4 text-center text-xs text-muted-foreground">
                            {t("definition.noConfig")}
                        </div>
                    )}
                </div>
                <ConfigPopoverResizeHandle
                    isResizing={isResizing}
                    onMouseDown={startPopoverResize}
                    onKeyDown={handleResizeKeyDown}
                />
            </div>
        );
    }

    if (!definition) return null;

    const hasPluginConfigPanel = !!plugin?.ConfigPanel;
    const showVisualConfig = hasPluginConfigPanel && !useRawEditor;
    const canRunUnit = !!plugin && !isTemplateUnit;

    // Position the popover below the click point, clamped to viewport
    const style: React.CSSProperties = {
        position: "fixed",
        left: position?.x ?? target.x,
        top: position?.y ?? target.y + 4,
        zIndex: 100,
        width: popoverSize.width,
        maxHeight: getConfigPopoverMaxHeight(currentPopoverOrigin()),
        ...(popoverSize.height !== null ? { height: popoverSize.height } : {}),
    };

    return (
        <div
            ref={ref}
            style={style}
            data-magic-md-config-popover="definition"
            className="relative flex flex-col rounded-xl border border-border bg-background shadow-xl overflow-hidden"
        >
            {/* Header — compact single-row controls */}
            <div
                data-magic-md-config-popover-header
                className={CONFIG_POPOVER_HEADER_CLASS}
                role="presentation"
                onMouseDownCapture={onHeaderMouseDown}
                title={t("definition.dragMoveConfig")}
            >
                <StatusDot status={execution?.status ?? "idle"} />
                <div className="min-w-0 flex-1">
                    <span
                        className="block text-[11px] font-semibold truncate"
                        title={t("definition.typeTitle", { type: definition.pluginName })}
                    >
                        {definition.id}
                    </span>
                </div>
                {isDragging && (
                    <span className="text-[10px] text-muted-foreground/70 shrink-0">
                        {t("definition.moving")}
                    </span>
                )}
                <DefinitionPanelTabs
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    outputDuration={execution?.duration ?? undefined}
                    configAccessory={
                        hasPluginConfigPanel ? (
                            <ConfigEditorModeButton
                                useRawEditor={useRawEditor}
                                onChange={setUseRawEditor}
                                embedded
                            />
                        ) : undefined
                    }
                />

                {canRunUnit && execution?.status === "running" ? (
                    <button
                        type="button"
                        className="flex h-6 shrink-0 items-center gap-1 rounded px-2 text-[10px] font-medium transition-colors theme-accent-button"
                        style={getThemeAccentStyle("error")}
                        onClick={() => cancelExecution(definition.id)}
                    >
                        <span className="flex items-center justify-center w-1.5 h-1.5 rounded-sm bg-background" />
                        {t("common.stop")}
                    </button>
                ) : canRunUnit ? (
                    <button
                        type="button"
                        className="flex h-6 shrink-0 items-center gap-1 rounded bg-primary px-2 text-[10px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                        onClick={handleRun}
                    >
                        <Play size={10} />
                        {t("common.run")}
                    </button>
                ) : plugin && isTemplateUnit ? (
                    <span className="text-[10px] shrink-0 text-muted-foreground">
                        {t("definition.template")}
                    </span>
                ) : (
                    <span
                        className="text-[10px] shrink-0 theme-accent-text"
                        style={getThemeAccentStyle("warning")}
                    >
                        {t("definition.notInstalled")}
                    </span>
                )}

                <button
                    type="button"
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                    onClick={onClose}
                    title={t("common.close")}
                >
                    <X size={14} />
                </button>
            </div>

            {/* Content */}
            <div className="min-h-0 flex-auto overflow-auto">
                {activeTab === "config" && (
                    <div className="p-3 space-y-2">
                        {isDirty && (
                            <DirtyStateBanner onSave={applyDraft} onDiscard={discardDraft} />
                        )}

                        {currentUseBinding && (useSourceConfig || templateParams || templateInputSchema) && (
                            <div className="mb-2 space-y-2">
                                {useSourceConfig && (
                                    <UseBindEditor
                                        useBinding={currentUseBinding}
                                        definitions={definitions}
                                        sourceConfig={useSourceConfig}
                                        importSymbols={importSymbols}
                                        locator={validatedLocator}
                                    />
                                )}
                                {(templateParams || templateInputSchema) && (
                                    <UseWithEditor
                                        useBinding={currentUseBinding}
                                        templateParams={templateParams ?? []}
                                        inputSchema={templateInputSchema}
                                        definitions={definitions}
                                        locator={validatedLocator}
                                    />
                                )}
                            </div>
                        )}

                        {/* Plugin's ConfigPanel or raw JSON editor */}
                        {showVisualConfig && plugin?.ConfigPanel ? (
                            <div>
                                <plugin.ConfigPanel
                                    value={draftConfig}
                                    onChange={updateDraft}
                                    context={configContext}
                                />
                            </div>
                        ) : (
                            <RawConfigEditor config={draftConfig} onChange={updateDraft} />
                        )}
                    </div>
                )}
                {activeTab === "output" && (
                    <div className="p-3">
                        {execution?.output ? (
                            <JsonTree value={execution.output} defaultExpandDepth={2} />
                        ) : (
                            <p className="text-xs text-muted-foreground text-center py-4">
                                {isTemplateUnit ? (
                                    t("instance.templateNotExecutable")
                                ) : (
                                    t("definition.noOutputRun")
                                )}
                            </p>
                        )}
                        {execution?.result && !execution.result.success && (
                            <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                                <div className="flex items-start gap-2">
                                    <span className="min-w-0 flex-1 break-words">
                                        {execution.result.message}
                                    </span>
                                    <button
                                        type="button"
                                        className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-destructive hover:bg-destructive/10"
                                        onClick={() => clearExecution(definition.id)}
                                    >
                                        <X size={10} className="inline-block mr-0.5" />
                                        {t("common.clear")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {(canRunUnit ||
                extensionUnitActions.length > 0) && (
                <SecondaryActionsBar
                    actions={
                        canRunUnit
                            ? plugin?.manifest.actions.filter(
                                  (a: ActionDeclaration) => !a.primary,
                              ) ?? []
                            : []
                    }
                    disabled={execution?.status === "running"}
                    onAction={(actionName) => {
                        void runAction(definition, actionName, definitions);
                    }}
                >
                    {extensionUnitActions.length > 0 ? (
                        <ExtensionUnitConfigActions unit={unitConfigContext} />
                    ) : null}
                </SecondaryActionsBar>
            )}
            <ConfigPopoverResizeHandle
                isResizing={isResizing}
                onMouseDown={startPopoverResize}
                onKeyDown={handleResizeKeyDown}
            />
        </div>
    );
}
